import { AxiosInstance } from 'axios';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { DumQueryApi } from '@src/Api/generated-global-event';
import { useDocumentEvent } from '@src/Common/Hooks/useDocumentEvent';
import useInactivityMonitor from '@src/Common/Hooks/useInactivityMonitor';
import { useStores } from '@src/Common/Hooks/useStores';
import useWindowDimensions from '@src/Common/Hooks/useWindowDimensions';
import AxiosInstanceGlobalEvent from '@src/Common/Services/Api/AxiosInstanceGlobalEvent';
import { monthLists } from '@src/Domain/DUMEvents/DUMEvents.const';
import {
  IDataTimeLine,
  IItemTimeLine
} from '@src/Domain/DUMEvents/DUMEvents.type';

import Tutorial from '@src/Components/Atoms/Tutorial/Tutorial';
import Maps from '@src/Components/Molecules/Maps/Maps';
import DefaultModal from '@src/Components/Molecules/Modal/DefaultModal/DefaultModal';
import ModalMessage from '@src/Components/Molecules/Modal/ModalMassage/ModalMessage';
import TimeLine from '@src/Components/Molecules/TimeLine/TimeLine';
import TimelineDetail from '@src/Components/Molecules/TimelineDetail/TimelineDetail';
import TooltipConsole from '@src/Components/Molecules/TooltipConsole/TooltipConsole';

import ButtonEvent from '@src/Components/Atoms/ButtonEvent/ButtonEvent';
import ButtonFilter from '@src/Components/Atoms/ButtonFilter/ButtonFilter';
import ButtonSubmission from '@src/Components/Atoms/ButtonSubmission/ButtonSubmission';
import SkeletonTimeline from '@src/Components/Atoms/Skeleton/SkeletonTimeline/SkeletonTimeline';
import SkeletonTimelineDetail from '@src/Components/Atoms/Skeleton/SkeletonTimelineDetail/SkeletonTimelineDetail';
import DetailEventPopup from '@src/Components/Molecules/DetailEventPopup/DetailEventPopup';
import TimelineEmptyData from '@src/Components/Molecules/TimelineEmptyData/TimelineEmptyData';
import AdvanceFilter from '@src/Components/Organisms/AdvanceFilter/AdvanceFilter';
import AllEventsDialog from '@src/Components/Organisms/AllEventsDialog/AllEventsDialog';
import MySubmission from '@src/Components/Organisms/MySubmissionsDialog/MySubmissionsDialog';
import { logEvent } from '@src/Domain/Analytic';
import { AnalyticsEvent } from '@src/Domain/Analytic/Analytic.type';
import { useAuth } from 'react-oidc-context';
import styles from './WorldMap.module.scss';

const lDEQa = (AI: AxiosInstance) => new DumQueryApi(undefined, '', AI);
const timeAnimation = Number(process.env.REACT_APP_ANIMATION_IDLE_TIME);

const TimelineContainer: React.FC<{
  isLoading: boolean;
  width: number;
  dataTimeLine: IDataTimeLine;
  isDetected: boolean;
  handleActiveTimeline: (item: IItemTimeLine) => void;
}> = ({ isLoading, width, dataTimeLine, isDetected, handleActiveTimeline }) => {
  const { UserStore } = useStores();
  if (isLoading) {
    return (
      <div className={styles['timeline-wrapper-loading']}>
        <div
          style={{
            width: width > 768 ? width * 0.5 : width * 0.9
          }}
        >
          <SkeletonTimeline containerWidth={width * 0.5} />
        </div>
      </div>
    );
  }
  if (dataTimeLine.list.length > 0) {
    return (
      <div id="timeline-wrapper" className={styles['timeline-wrapper']}>
        <TimeLine
          dataItems={dataTimeLine.list}
          starYear={dataTimeLine.starYear ?? 0}
          endYear={dataTimeLine.endYear ?? 0}
          isDetectedAction={UserStore.isDetectedAction || isDetected}
          onActiveTimeline={(item) => handleActiveTimeline(item)}
        />
      </div>
    );
  }
  return <TimelineEmptyData />;
};

const TimelineDetailContainer: React.FC<{
  isLoading: boolean;
  width: number;
  date: string;
  countLocation: number;
  totalEventDum: number;
  totalEventCasefile: number;
}> = ({
  isLoading,
  width,
  date,
  countLocation,
  totalEventDum,
  totalEventCasefile
}) => {
  if (isLoading) {
    return (
      <div
        style={{
          width: width > 768 ? width * 0.1 : width * 0.2
        }}
      >
        <SkeletonTimelineDetail />
      </div>
    );
  }
  return (
    <TimelineDetail
      date={date}
      countLocation={countLocation}
      event={totalEventDum}
      repo={totalEventCasefile}
    />
  );
};

const WorldMap = observer(function WorldMap() {
  const { width } = useWindowDimensions();
  const { isDetected, setIsDetected } = useDocumentEvent();

  const {
    DUMEventStore,
    UserStore,
    AdvanceFilterStore,
    AllEventStore,
    MySubmissionStore,
    FormStore
  } = useStores();
  const { dataTimeLine, status, totalEventCasefile, totalEventDUM, totalData } =
    DUMEventStore.DUMEventDatas;

  const [showTutorial, setShowTutorial] = useState<boolean>(true);

  const fetchDUMEvent = lDEQa(AxiosInstanceGlobalEvent('global-event'));

  const handleLoadDUMEvents = useCallback(() => {
    DUMEventStore.getDUMEvent(fetchDUMEvent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [DUMEventStore]);

  useEffect(() => {
    handleLoadDUMEvents();
  }, [handleLoadDUMEvents]);

  useEffect(() => {
    if (
      AdvanceFilterStore.initialize &&
      DUMEventStore.DUMEventDatas.dataTimeLine.list &&
      DUMEventStore.DUMEventDatas.dataTimeLine.list.length > 0
    ) {
      DUMEventStore.setActiveTimeLine(
        fetchDUMEvent,
        DUMEventStore.DUMEventDatas.dataTimeLine.list[0]
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [DUMEventStore.overviews]);

  const handleActiveTimeline = (item: IItemTimeLine) => {
    DUMEventStore.setActiveTimeLine(fetchDUMEvent, item, true);
  };

  const handleInactivity = (val: boolean) => {
    setIsDetected(val);
    UserStore.updateDetectedAction(val);
  };
  useInactivityMonitor(timeAnimation, handleInactivity);

  const { isAuthenticated, isLoading } = useAuth();

  const badgeAdvanceFilter = useMemo(() => {
    let badge = 0;
    if (
      AdvanceFilterStore.filters.anomalyRating !== undefined &&
      AdvanceFilterStore.filters.anomalyRating.some(
        (v) => v !== undefined && v !== -1
      )
    ) {
      badge++;
    }
    if (
      AdvanceFilterStore.filters.contries !== undefined &&
      AdvanceFilterStore.filters.contries.some((v) => v !== undefined)
    ) {
      badge++;
    }
    if (
      AdvanceFilterStore.filters.ecosystem !== undefined &&
      AdvanceFilterStore.filters.ecosystem.some(
        (v) => v !== undefined && v !== -1
      )
    ) {
      badge++;
    }
    if (AdvanceFilterStore.filters.location !== undefined) {
      badge++;
    }
    if (
      AdvanceFilterStore.filters.year.from !== undefined &&
      AdvanceFilterStore.filters.year.end !== undefined &&
      AdvanceFilterStore.filters.year.from !== AdvanceFilterStore.allYears[0] &&
      AdvanceFilterStore.filters.year.end !==
        AdvanceFilterStore.allYears[AdvanceFilterStore.allYears.length - 1]
    ) {
      badge++;
    }
    return badge;
  }, [
    AdvanceFilterStore.allYears,
    AdvanceFilterStore.filters.anomalyRating,
    AdvanceFilterStore.filters.contries,
    AdvanceFilterStore.filters.ecosystem,
    AdvanceFilterStore.filters.location,
    AdvanceFilterStore.filters.year.end,
    AdvanceFilterStore.filters.year.from
  ]);

  return (
    <>
      <DefaultModal
        show={showTutorial && width <= 600}
        onClose={() => setShowTutorial(false)}
      >
        <div className={styles['tutorial-mobile-wrapper']}>
          <Tutorial onClose={() => setShowTutorial(false)} />
        </div>
      </DefaultModal>
      <ModalMessage
        show={status.isError}
        headerTitle="Error"
        buttonTitle="Try Again"
        message={status.message}
        onClick={() => window.location.reload()}
      />
      <section className={styles['Container']}>
        {showTutorial && (
          <div className={styles['tutorial-wrapper']}>
            <Tutorial
              type={width >= 1280 ? 'click' : 'drag'}
              onClose={() => setShowTutorial((prev) => !prev)}
            />
          </div>
        )}
        <Maps />
        <TimelineContainer
          isLoading={status.isLoading}
          width={width}
          dataTimeLine={dataTimeLine}
          isDetected={isDetected}
          handleActiveTimeline={handleActiveTimeline}
        />

        <div className={styles['timeline-detail']}>
          <TimelineDetailContainer
            isLoading={status.isLoading}
            width={width}
            date={`${
              monthLists[
                dataTimeLine.active?.month ? dataTimeLine.active?.month - 1 : 0
              ]
            } ${dataTimeLine.active?.year}`}
            countLocation={totalData}
            totalEventDum={totalEventDUM}
            totalEventCasefile={totalEventCasefile}
          />
        </div>

        <div className={styles['position_tooltip-console']}>
          <TooltipConsole />
        </div>
      </section>
      {AdvanceFilterStore.show ? (
        <AdvanceFilter
          show={AdvanceFilterStore.show}
          onClose={() => {
            AdvanceFilterStore.show = false;
          }}
        />
      ) : null}
      {AllEventStore.show ? (
        <AllEventsDialog
          show={AllEventStore.show}
          onClose={() => {
            AllEventStore.show = !AllEventStore.show;
            AllEventStore.filters = {
              year: undefined,
              location: 1,
              contries: undefined,
              ecosystem: undefined,
              anomalyRating: undefined,
              searchValue: ''
            };
          }}
        />
      ) : null}
      <div className={styles['ButtonContainer']}>
        <ButtonFilter
          badge={badgeAdvanceFilter}
          onClick={() => {
            AdvanceFilterStore.show = !AdvanceFilterStore.show;
            logEvent(
              AnalyticsEvent.BUTTON_CLICKED,
              'DUM Sidebar Advance Filter Button Click'
            );
          }}
        />
        <ButtonEvent
          onClick={() => {
            AllEventStore.show = !AllEventStore.show;
            logEvent(
              AnalyticsEvent.BUTTON_CLICKED,
              'DUM Sidebar All Event Button Click'
            );
          }}
        />
        {!isLoading && isAuthenticated ? (
          <ButtonSubmission
            onClick={() => {
              MySubmissionStore.show = !MySubmissionStore.show;
              logEvent(
                AnalyticsEvent.BUTTON_CLICKED,
                'DUM Sidebar My Submission Button Click'
              );
            }}
          />
        ) : null}
      </div>
      {!isLoading && isAuthenticated && MySubmissionStore.show ? (
        <MySubmission
          show={MySubmissionStore.show}
          onClose={() => (MySubmissionStore.show = !MySubmissionStore.show)}
          onSubmitEvent={() => {
            MySubmissionStore.show = !MySubmissionStore.show;
            FormStore.clearCoordinate();
            FormStore.showModal();
            logEvent(
              AnalyticsEvent.BUTTON_CLICKED,
              'DUM My Submission Submit Event Button Click'
            );
          }}
        />
      ) : null}
      {MySubmissionStore.showDetail && MySubmissionStore.detailData ? (
        <DetailEventPopup
          onClose={() => {
            DUMEventStore.selectedDetailDumGlobalIdIndex = undefined;
            MySubmissionStore.showDetail = false;
            MySubmissionStore.detailData = undefined;
          }}
          showAddBtn={false}
          eventData={MySubmissionStore.detailData}
        />
      ) : null}
    </>
  );
});

export default WorldMap;
